import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import '../styles/nassar-theme.css';
import './AdminLogin.css';

const AdminLogin = ({ setIsAuthenticated }) => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        password: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    // Check for session expiry message on component mount
    React.useEffect(() => {
        const manualLogout = sessionStorage.getItem('manual_logout');
        const sessionExpiredMsg = sessionStorage.getItem('session_expired_message');
        
        // Clear manual logout flag
        if (manualLogout) {
            sessionStorage.removeItem('manual_logout');
        }
        
        // Only show session expired message if it's not a manual logout
        if (sessionExpiredMsg && !manualLogout) {
            setErrorMessage(sessionExpiredMsg);
            sessionStorage.removeItem('session_expired_message');
        }
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (errorMessage) {
            setErrorMessage('');
        }
    };

    const validateForm = () => {
        if (!formData.name.trim() || !formData.password.trim()) {
            setErrorMessage('اسم المستخدم وكلمة المرور مطلوبة');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;

        setIsLoading(true);
        setErrorMessage('');

        try {
            const response = await axiosInstance.post('/login/', {
                name: formData.name,
                password: formData.password
            });

            if (response.data.jwt) {
                localStorage.setItem('nassar_token', response.data.jwt);
                
                setIsAuthenticated?.(true);
                navigate('/dashboard/clearance-files');
            }
        } catch (error) {
            const errorMsg = error.response?.data?.detail || 
                           error.response?.data?.error || 
                           'اسم المستخدم أو كلمة المرور غير صحيحة';
            setErrorMessage(errorMsg);
            setFormData(prev => ({ ...prev, password: '' }));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="login-page">
            <div className="login-background"></div>
            
            <div className="login-container">
                <div className="login-card">
                    {/* Logo and Branding */}
                    <div className="login-header">
                        <div className="login-logo">
                            <img 
                                src={`${process.env.PUBLIC_URL}/nassar_logo_login.png`} 
                                alt="نصّار للخدمات التجارية" 
                                className="logo-image"
                            />
                        </div>
                        <div className="login-divider"></div>
                    </div>

                    {/* Login Form */}
                    <form onSubmit={handleSubmit} className="login-form">
                        <div className="form-group">
                            <label htmlFor="name" className="form-label">
                                <svg className="label-icon" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                                </svg>
                                <span>اسم المستخدم</span>
                            </label>
                            <input 
                                type="text" 
                                id="name" 
                                name="name" 
                                className="form-input login-input" 
                                placeholder="أدخل اسم المستخدم"
                                value={formData.name}
                                onChange={handleInputChange}
                                autoComplete="username"
                                disabled={isLoading}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="password" className="form-label">
                                <svg className="label-icon" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                </svg>
                                <span>كلمة المرور</span>
                            </label>
                            <div className="password-input-wrapper">
                                <input 
                                    type={showPassword ? "text" : "password"} 
                                    id="password" 
                                    name="password" 
                                    className="form-input login-input" 
                                    placeholder="أدخل كلمة المرور"
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    autoComplete="current-password"
                                    disabled={isLoading}
                                />
                                <button 
                                    type="button" 
                                    className="password-toggle"
                                    onClick={() => setShowPassword(!showPassword)}
                                    disabled={isLoading}
                                >
                                    {showPassword ? (
                                        <svg fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                                            <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                                        </svg>
                                    ) : (
                                        <svg fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                            <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Error Message */}
                        {errorMessage && (
                            <div className="error-message">
                                <svg className="error-icon" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                <span>{errorMessage}</span>
                            </div>
                        )}

                        <button 
                            type="submit" 
                            className={`login-btn ${isLoading ? 'loading' : ''}`}
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <div className="spinner"></div>
                                    <span>جاري تسجيل الدخول...</span>
                                </>
                            ) : (
                                <>
                                    <svg className="btn-icon" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M3 3a1 1 0 011 1v12a1 1 0 11-2 0V4a1 1 0 011-1zm7.707 3.293a1 1 0 010 1.414L9.414 9H17a1 1 0 110 2H9.414l1.293 1.293a1 1 0 01-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    <span>تسجيل الدخول</span>
                                </>
                            )}
                        </button>
                    </form>

                    {/* Footer */}
                    <div className="login-footer">
                        <p className="copyright">
                            © 2026 نصّار للخدمات التجارية. جميع الحقوق محفوظة.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminLogin;

